export interface ErrorDetails {
  shortDescription: string;
  explanation: string;
}

export interface ErrorCodeProvider {
  version: string;
  lookup: (code: number, group?: number) => ErrorDetails | undefined;
}

export type ErrorCodeLookupFn = (
  code: number,
  group?: number
) => ErrorDetails | undefined;

// Factory function to create providers
export function createErrorCodeProvider(
  version: string,
  lookupFn: ErrorCodeLookupFn
): ErrorCodeProvider {
  return {
    version,
    lookup: lookupFn,
  };
}

// Version range mapping - maps version ranges to provider versions
interface VersionRangeEntry {
  providerVersion: string;
  minVersion: string;
  maxVersion: string;
}

const versionRanges: VersionRangeEntry[] = [];

// Registry to manage all providers
const providers: Map<string, ErrorCodeProvider> = new Map();

/**
 * Parse semantic version string into comparable numbers
 * e.g. "1.2" -> [1, 2]
 */
function parseVersion(version: string): number[] {
  return version
    .split('.')
    .map((v) => parseInt(v, 10))
    .slice(0, 2);
}

/**
 * Compare two semantic versions
 * Returns: -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
function compareVersions(v1: string, v2: string): number {
  const v1Parts = parseVersion(v1);
  const v2Parts = parseVersion(v2);

  for (let i = 0; i < 2; i++) {
    const part1 = v1Parts[i] || 0;
    const part2 = v2Parts[i] || 0;

    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }

  return 0;
}

export function registerErrorCodeProvider(provider: ErrorCodeProvider): void {
  providers.set(provider.version, provider);
}

/**
 * Register a version range mapping
 * e.g. registerVersionRange("1.0", ["1.0", "1.1"]) maps versions 1.0-1.1 to provider "1.0"
 */
export function registerVersionRange(
  providerVersion: string,
  versions: string[]
): void {
  if (versions.length < 2) {
    console.warn(
      `Version range for provider "${providerVersion}" must have at least 2 versions (min and max)`
    );
    return;
  }

  const minVersion = versions[0];
  const maxVersion = versions[versions.length - 1];

  versionRanges.push({ providerVersion, minVersion, maxVersion });
  // Sort by minVersion descending so we check newest ranges first
  versionRanges.sort((a, b) => compareVersions(b.minVersion, a.minVersion));
}

/**
 * Get the appropriate error code provider for a given device version
 */
export function getErrorCodeProvider(
  deviceVersion: string
): ErrorCodeProvider | undefined {
  // First try exact match
  if (providers.has(deviceVersion)) {
    return providers.get(deviceVersion);
  }

  // Then try version range matching
  for (const range of versionRanges) {
    if (
      compareVersions(deviceVersion, range.minVersion) >= 0 &&
      compareVersions(deviceVersion, range.maxVersion) <= 0
    ) {
      return providers.get(range.providerVersion);
    }
  }

  // Fallback to first registered provider
  return providers.size > 0 ? Array.from(providers.values())[0] : undefined;
}

export function getDefaultErrorCodeProvider(): ErrorCodeProvider | undefined {
  // Return the first registered provider as default
  return providers.size > 0 ? Array.from(providers.values())[0] : undefined;
}

export function getAllErrorCodeProviders(): ErrorCodeProvider[] {
  return Array.from(providers.values());
}
